import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  Avatar,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Backdrop,
  CircularProgress,
  Skeleton,
  Grid,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import {
  Edit,
  Delete,
  Visibility,
  FilterList,
  ExpandMore,
  WorkOutline,
  Add,
  Search,
  RestartAlt,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { encryptId } from "../../Components/CryptoComponent";
import SelectInput from "../../Components/InputComponents/SelectInput";
import { errorNotify } from "../../Components/ToastComponent/ToastNotification";
import TextInput from "../../Components/InputComponents/TextInput";
import HorizontalStageBox from "../../CandidateView/Dashboard/Components/HorizontalStageBox";
import SideStageCard from "../../Views/Candidate/Components/SideStageCard";
import ClientSideStageCard from "./Components/ClientSideStageCard";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const ClientCandidateList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const candidateId =
    sessionStorage?.getItem(`SWA_UserId`) ||
    localStorage?.getItem(`SWA_UserId`);

  const [CandidateData, setCandidateData] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loaderOpen, setLoaderOpen] = useState(true);

  const [expandedIds, setExpandedIds] = useState("");

  const [selectedJobListIds, setSelectedJobListIds] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [procedureData, setProcedureData] = useState([]);


  const [expandedJobId, setExpandedJobId] = useState(null);

  const handleJobToggle = (jobId) => {
    setExpandedJobId(prev => (prev === jobId ? null : jobId));
  };


  const handleAccordionChange = (id) => {

    setExpandedIds(prev =>
      prev === id?.CI_Id ? "" : id?.CI_Id
    );

    const jobIds =
      id?.applied_jobs?.map(job => job.JA_ApplicationId) || [];

    setSelectedCandidateId(id?.CI_Id);
    setSelectedJobListIds(jobIds);

    navigate("/Candidates", { replace: true });
  };


  const getJobDataForCandidate = async (candidateId, jobIds) => {
    if (!candidateId || !jobIds?.length) {
      setProcedureData([]);
      return;
    }

    try {
      const responses = await Promise.all(
        jobIds.map(jobId =>
          axiosAPI.post(`/clientportal/JobData`, {
            JobApplicationId: jobId,
            CandidateId: candidateId,
          })
        )
      );

      const procedures = responses
        .map((res, index) => {
          const rawValue = res?.data?.data?.[0]?.CRD_FieldValue;

          if (!rawValue) return null;

          try {
            return {
              jobApplicationId: jobIds[index],
              data: JSON.parse(rawValue),
            };
          } catch (err) {
            console.error("Invalid JSON", err);
            return null;
          }
        })
        .filter(Boolean);

      setProcedureData(procedures);

    } catch (err) {
      console.error(err);
      setProcedureData([]);
    }
  };


  useEffect(() => {
    if (selectedCandidateId && selectedJobListIds.length) {
      getJobDataForCandidate(
        selectedCandidateId,
        selectedJobListIds
      );
    }
  }, [selectedCandidateId, selectedJobListIds]);

  const getProcedureByJobId = (jobApplicationId) => {
    return procedureData?.find(
      p => p.jobApplicationId === jobApplicationId
    );
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApplyFilters = () => {
    getData();
    setPage(0);
  };

  const [filterData, setFilterData] = useState({
    name: "",
    profession: "",
    jobapplication: "",
    jobid: "",
    interviewdate: "",
  });

  const paginationState = {
    pagination: { page, rowsPerPage },
    filterData: filterData,
    from: `Candidates`,
  };

  const handleResetFilters = () => {
    const resetFilters = {
      name: "",
      profession: "",
      jobapplication: "",
      jobid: "",
      interviewdate: "",
    };

    const isAlreadyReset = Object.entries(resetFilters).every(
      ([key, value]) => filterData[key] === value
    );

    if (!isAlreadyReset) {
      setFilterData(resetFilters);
      getData(resetFilters);
    }

    navigate(`/Candidates`, { replace: true });
  };

  const navigatetoView = (id) => {
    navigate(`/Candidates/View`, {
      state: {
        ...paginationState,
        selectedId: JSON.stringify(id),
        from: 'Jobs'
      },
    });
  };



  const [professionList, setProfessionList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [jobList, setJobList] = useState([]);

  const getProfession = async () => {
    try {
      const res = await axiosAPI.get(`/Profession/list`);
      setProfessionList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getStatus = async () => {
    try {
      const res = await axiosAPI.get(`/JobApplicationStatus/list`);
      setStatusList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const clientId = sessionStorage?.getItem(`SWA_UserId`) || localStorage?.getItem(`SWA_UserId`);

  const getJobs = async () => {
    const trimmedFilters = {
      JD_Title: ``,
      JD_Country: ``,
      JD_Qualification: ``,
      JD_Profession: ``,
      JD_Status: ``,
    };

    try {
      const res = await axiosAPI?.post(`/clientportal/joblist/${clientId}`, trimmedFilters);
      setJobList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilterData({
      ...filterData,
      [name]: value,
    });
  };

  const getData = (filter = filterData) => {
    const trimmedFilters = {
      CI_Name: filter?.name, // name
      CI_Profession: filter?.profession, // profession //
      JASL_Id: filter?.jobapplication, // jpb application status //
      Jobid: filter?.jobid, // job list
      JA_InterviewDate: filter?.interviewdate, // date
    };

    axiosAPI
      ?.post(`clientportal/Candidatelist/${candidateId}`, trimmedFilters)

      .then((res) => {
        setCandidateData(res?.data?.data);
        setLoaderOpen(false);
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getProfession();
    getJobs();
    getStatus();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (location?.state?.pagination) {
      setPage(location.state.pagination.page || 0);
      setRowsPerPage(location.state.pagination.rowsPerPage || 25);
    }

    if (location?.state?.filterData) {
      setFilterData(location.state.filterData);
      getData(location.state.filterData); // fetch with restored filters
    } else {
      getData();
    }
  }, [location?.state, page, rowsPerPage]);

  const isMobile = useMediaQuery("(max-width: 1220px)");

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          Candidates
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ width: "100%", mb: 2 }}>
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Name
            </Typography>
            <TextField
              placeholder="Name"
              variant="outlined"
              size="small"
              value={filterData.name}
              onChange={(e) =>
                setFilterData((prev) => ({ ...prev, name: e.target.value }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          {/* Profession */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Profession
            </Typography>
            <SelectInput
              value={filterData?.profession}
              name="profession"
              onChange={handleFilterChange}
              options={professionList?.map((item) => ({
                value: item?.PI_Id,
                label: item?.PI_Name,
              }))}
              placeholder="Select Profession"
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          {/*  Job Application */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Job Application Status
            </Typography>
            <SelectInput
              value={filterData.jobapplication}
              name="jobapplication"
              onChange={handleFilterChange}
              options={statusList?.map((item) => ({
                value: item?.JASL_Id,
                label: item?.JASL_Name,
              }))}
              placeholder="Select Status"
            />
          </Grid>

          {/* Job */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Job
            </Typography>
            <SelectInput
              value={filterData.jobid}
              name="jobid"
              onChange={handleFilterChange}
              options={jobList?.map((item) => ({
                value: item?.JD_Id,
                label: item?.JD_Title,
              }))}
              placeholder="Select Job"
            />
          </Grid>

          {/*  Interview Date */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Interview Date
            </Typography>
            <TextInput
              placeholder="Name"
              variant="outlined"
              size="small"
              type="date"
              value={filterData.interviewdate}
              onChange={(e) =>
                setFilterData((prev) => ({
                  ...prev,
                  interviewdate: e.target.value,
                }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>
        </Grid>

        {/* job list */}
      </Box>

      {/* Buttons */}
      <Box
        sx={{
          width: "100%", // Full width
          display: "flex",
          justifyContent: "flex-end", // Align buttons to the right
          alignItems: "flex-end",
          gap: 1,
          my: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<Search />}
          onClick={handleApplyFilters}
          sx={{
            textTransform: "capitalize",
            color: "white",
            background: "#006CB8",
          }}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<RestartAlt />}
          onClick={handleResetFilters}
          sx={{ textTransform: "capitalize" }}
        >
          Reset
        </Button>
      </Box>

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <TableContainer component={Paper} >
          <Table stickyHeader>
            <TableHead >
              <TableRow >
                <TableCell sx={{ color: "#fff", background: "#2C5F8E", borderRadius: "10px 0px 0px 0px" }} />
                <TableCell sx={{ color: "#fff", background: "#2C5F8E" }}>#</TableCell>
                <TableCell sx={{ color: "#fff", background: "#2C5F8E" }}>Name</TableCell>

                <TableCell sx={{ color: "#fff", background: "#2C5F8E" }}>Designation</TableCell>
                <TableCell sx={{ color: "#fff", background: "#2C5F8E" }}>Profession</TableCell>
                <TableCell sx={{ color: "#fff", background: "#2C5F8E" }}>Applied Jobs</TableCell>
                <TableCell sx={{ color: "#fff", background: "#2C5F8E", textAlign: "center", borderRadius: "0px 10px 0px 0px" }}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loaderOpen ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <TableRow key={idx}>
                    <TableCell colSpan={9}>
                      <Box sx={{ height: 50, backgroundColor: "#f0f0f0" }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : CandidateData?.length > 0 ? (
                CandidateData?.map((c, index) => (
                  <React.Fragment key={c.CI_Id}>
                    <TableRow
                      sx={{ backgroundColor: index % 2 !== 0 ? "#EBF2FF" : "inherit" }}
                    >
                      <TableCell>
                        {c?.applied_jobs?.length > 0 && (
                          <IconButton onClick={() => handleAccordionChange(c)}>
                            <ExpandMore
                              sx={{
                                transform: expandedIds === c.CI_Id ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "0.3s ease-in-out",
                                border: "2px solid #2C5F8E",
                                color: "#2C5F8E",
                                borderRadius: "50%",
                              }}
                            />
                          </IconButton>
                        )}
                      </TableCell>

                      <TableCell>{index + 1 + page * rowsPerPage}</TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center" sx={{ cursor: "pointer" }} onClick={() => navigatetoView(c)}>
                          <Avatar
                            src={
                              c?.CI_Photo
                                ? `${process.env.REACT_APP_BASE_URL}/${c.CI_Photo}`
                                : undefined
                            }
                            sx={{ width: 46, height: 46, mr: 1 }}
                          >
                            {c?.CI_Name?.[0] || ""}
                          </Avatar>

                          <Typography fontFamily="Geist, sans-serif">
                            {c?.CI_Name}
                            {/* {c?.CI_Alias ? `(${c?.CI_Alias})` : ""} */}
                          </Typography>
                        </Box>
                      </TableCell>



                      <TableCell>
                        <Typography fontFamily="Geist, sans-serif">
                          {c?.CI_Designation || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography fontFamily="Geist, sans-serif">
                          {c?.Applied_profession_Name || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography fontFamily="Geist, sans-serif">
                          {c?.applied_jobs?.length || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigatetoView(c)}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Row for Applied Jobs */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                        <Collapse in={expandedIds === c.CI_Id} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1, border: "1px solid #ccc", borderRadius: 2 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ backgroundColor: "#71aee5" }}>
                                  <TableCell sx={{ color: "#fff" }}> </TableCell>
                                  <TableCell sx={{ color: "#fff" }}>#</TableCell>
                                  <TableCell sx={{ color: "#fff" }}>Job Title</TableCell>
                                  <TableCell sx={{ color: "#fff" }}>Applied Date</TableCell>
                                  <TableCell sx={{ color: "#fff" }}>Interview Status</TableCell>
                                  <TableCell sx={{ color: "#fff" }}>Interview Date</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {c?.applied_jobs?.length > 0 ? (
                                  c.applied_jobs.map((j, i) => {
                                    const procedure = getProcedureByJobId(j.JA_ApplicationId);

                                    return (
                                      <React.Fragment key={j.JA_ApplicationId}>
                                        {/* Job row */}
                                        <TableRow>
                                          <TableCell>
                                            <IconButton size="small" onClick={() => handleJobToggle(j.JA_ApplicationId)}>
                                              <ExpandMore
                                                sx={{
                                                  transform:
                                                    expandedJobId === j.JA_ApplicationId
                                                      ? "rotate(180deg)"
                                                      : "rotate(0deg)",
                                                  transition: "0.3s",
                                                }}
                                              />
                                            </IconButton>
                                          </TableCell>

                                          <TableCell>{i + 1}</TableCell>
                                          <TableCell>{j?.job_applied || "-"}</TableCell>
                                          <TableCell>{j?.JA_AppliedDate || "-"}</TableCell>
                                          <TableCell>{j?.JobApplication_status || "-"}</TableCell>
                                          <TableCell>{j?.JA_InterviewDate || "-"}</TableCell>
                                        </TableRow>


                                        {/* Procedure row */}
                                        <TableRow>
                                          <TableCell colSpan={6} sx={{ p: 0 }}>
                                            <Collapse
                                              in={expandedJobId === j.JA_ApplicationId}
                                              timeout="auto"
                                              unmountOnExit
                                            >
                                              <Box sx={{ pl: 4, py: 2, backgroundColor: "#f7f9fc" }}>
                                                {procedure ? (
                                                  <ClientSideStageCard originalValue2={procedure.data} />
                                                ) : (
                                                  <Typography fontSize={13} color="text.disabled">
                                                    No procedure data
                                                  </Typography>
                                                )}
                                              </Box>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>

                                      </React.Fragment>
                                    );
                                  })
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={5} align="center">
                                      No jobs linked
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>

                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography fontFamily="Geist, sans-serif" variant="h6">
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={CandidateData?.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      />
    </Paper>
  );
};

export default ClientCandidateList;
